import { useState, useMemo } from 'react'
import {
  getAllPosProducts, getRecipes, getRecipeByProductId, calculateRecipeCost,
  getProductCostCard, getInventoryItems, getPosSales, getOverheads, getOverheadAllocations
} from '../../lib/posStore'
import { useDepartment } from '../../context/DepartmentContext'
import { POS_DEPARTMENTS } from '../../data/posSeed'
import {
  DollarSign, AlertTriangle, Check, TrendingUp, TrendingDown,
  Calculator, Search, Filter, Sliders
} from 'lucide-react'

const TABS = [
  { id: 'costCards', label: 'Cost Cards' },
  { id: 'simulation', label: 'Cost Simulation' },
  { id: 'dailyCogs', label: 'Daily COGS' },
]

function fmt(n) { return `R ${(n || 0).toFixed(2)}` }
function fmtPct(n) { return `${(n || 0).toFixed(1)}%` }

function StatusBadge({ status }) {
  const styles = {
    healthy: 'bg-green-900/40 text-green-400 border-green-800',
    warning: 'bg-amber-900/40 text-amber-400 border-amber-800',
    loss: 'bg-red-900/40 text-red-400 border-red-800',
  }
  const labels = { healthy: 'Healthy', warning: 'Warning', loss: 'Loss-Making' }
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 border ${styles[status] || styles.warning}`}>
      {labels[status] || status}
    </span>
  )
}

export default function CostingEngine() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('costCards')
  const [department, setDepartment] = useState('')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  // Simulation state
  const [selectedIngredient, setSelectedIngredient] = useState('')
  const [simulatedCost, setSimulatedCost] = useState('')

  // Daily COGS state
  const today = new Date().toISOString().split('T')[0]
  const [cogsDate, setCogsDate] = useState(today)

  // ── Data ──

  const products = useMemo(() => getAllPosProducts(currentDepartmentId), [currentDepartmentId])
  const recipes = useMemo(() => getRecipes(currentDepartmentId), [currentDepartmentId])
  const inventoryItems = useMemo(() => getInventoryItems(currentDepartmentId), [currentDepartmentId])

  const costCards = useMemo(() => {
    return products.map(p => getProductCostCard(p.id)).filter(Boolean)
  }, [products])

  const filteredCards = useMemo(() => {
    let cards = costCards
    if (department) cards = cards.filter(c => c.product.department === department)
    if (search) {
      const q = search.toLowerCase()
      cards = cards.filter(c =>
        c.product.name.toLowerCase().includes(q) ||
        c.product.code.toLowerCase().includes(q)
      )
    }
    return cards.sort((a, b) => a.grossMargin - b.grossMargin)
  }, [costCards, department, search])

  // Summary stats
  const stats = useMemo(() => {
    const total = filteredCards.length
    const margins = filteredCards.map(c => c.grossMargin)
    const avgMargin = margins.length > 0 ? margins.reduce((s, v) => s + v, 0) / margins.length : 0
    const lossCount = filteredCards.filter(c => c.status === 'loss').length
    const warningCount = filteredCards.filter(c => c.status === 'warning').length
    const healthyCount = filteredCards.filter(c => c.status === 'healthy').length
    return { total, avgMargin, lossCount, warningCount, healthyCount }
  }, [filteredCards])

  // ── Simulation data ──

  const simulationResults = useMemo(() => {
    if (!selectedIngredient || simulatedCost === '') return []
    const simCost = Number(simulatedCost)
    if (isNaN(simCost) || simCost < 0) return []

    const invItem = inventoryItems.find(i => i.id === selectedIngredient)
    if (!invItem) return []

    const currentUnitCost = invItem.lastPurchasePrice || invItem.avgCost || 0

    // Find all recipes using this ingredient
    const affected = []
    for (const recipe of recipes) {
      if (!recipe.ingredients) continue
      const usesIngredient = recipe.ingredients.some(ing => ing.inventoryItemId === selectedIngredient)
      if (!usesIngredient) continue

      const product = products.find(p => p.id === recipe.productId)
      if (!product) continue

      // Current cost
      const currentCogs = calculateRecipeCost(recipe)
      const currentMargin = product.sellingPrice > 0
        ? ((product.sellingPrice - currentCogs) / product.sellingPrice) * 100 : 0

      // Simulated cost: recalculate with new price
      let simCogs = 0
      for (const ing of recipe.ingredients) {
        if (ing.inventoryItemId === selectedIngredient) {
          let costPerUnit = simCost
          if (invItem.unit === 'kg' && ing.unit === 'g') costPerUnit = simCost / 1000
          else if (invItem.unit === 'L' && ing.unit === 'ml') costPerUnit = simCost / 1000
          simCogs += costPerUnit * (Number(ing.qty) || 0)
        } else {
          const otherItem = inventoryItems.find(i => i.id === ing.inventoryItemId)
          const otherCost = otherItem?.lastPurchasePrice || otherItem?.avgCost || 0
          let costPerUnit = otherCost
          if (otherItem?.unit === 'kg' && ing.unit === 'g') costPerUnit = otherCost / 1000
          else if (otherItem?.unit === 'L' && ing.unit === 'ml') costPerUnit = otherCost / 1000
          simCogs += costPerUnit * (Number(ing.qty) || 0)
        }
      }
      if (recipe.batchYield && recipe.batchYield > 1) simCogs = simCogs / recipe.batchYield

      const simMargin = product.sellingPrice > 0
        ? ((product.sellingPrice - simCogs) / product.sellingPrice) * 100 : 0

      // Minimum selling price for 30% margin
      const targetMargin = 0.30
      const minSellingPrice = simCogs / (1 - targetMargin)

      affected.push({
        product,
        currentCogs,
        currentMargin,
        simCogs,
        simMargin,
        becomesLoss: simMargin < 0 && currentMargin >= 0,
        minSellingPrice,
        needsPriceIncrease: simMargin < 30,
      })
    }

    return affected.sort((a, b) => a.simMargin - b.simMargin)
  }, [selectedIngredient, simulatedCost, recipes, products, inventoryItems])

  const selectedIngredientData = useMemo(() => {
    if (!selectedIngredient) return null
    return inventoryItems.find(i => i.id === selectedIngredient)
  }, [selectedIngredient, inventoryItems])

  // ── Daily COGS data ──

  const dailyCogsData = useMemo(() => {
    const sales = getPosSales(currentDepartmentId, cogsDate, cogsDate)
    const validSales = sales.filter(s => !s.voided)

    // Aggregate sales by product
    const productSales = {}
    for (const sale of validSales) {
      if (!sale.items) continue
      for (const item of sale.items) {
        const key = item.productId
        if (!productSales[key]) {
          productSales[key] = { productId: key, productName: item.productName || '', qty: 0, revenue: 0 }
        }
        productSales[key].qty += Number(item.qty) || 1
        productSales[key].revenue += (Number(item.price) || 0) * (Number(item.qty) || 1)
      }
    }

    const rows = []
    let totals = { qty: 0, revenue: 0, theoreticalCogs: 0, actualCogs: 0 }

    for (const [productId, data] of Object.entries(productSales)) {
      const product = products.find(p => p.id === productId)
      const recipe = getRecipeByProductId(productId)
      const theoreticalUnitCogs = recipe ? calculateRecipeCost(recipe) : (product?.cost || 0)
      const theoreticalCogs = theoreticalUnitCogs * data.qty
      // Actual COGS would come from real stock usage; use theoretical as baseline with slight variance
      const actualCogs = theoreticalCogs // In a real system, actual COGS from stock movements
      const variance = theoreticalCogs > 0 ? ((actualCogs - theoreticalCogs) / theoreticalCogs) * 100 : 0

      rows.push({
        productId,
        productName: product?.name || data.productName || 'Unknown',
        qty: data.qty,
        revenue: data.revenue,
        theoreticalCogs,
        actualCogs,
        variance,
        flagged: Math.abs(variance) > 15,
      })

      totals.qty += data.qty
      totals.revenue += data.revenue
      totals.theoreticalCogs += theoreticalCogs
      totals.actualCogs += actualCogs
    }

    totals.variance = totals.theoreticalCogs > 0
      ? ((totals.actualCogs - totals.theoreticalCogs) / totals.theoreticalCogs) * 100 : 0

    return { rows: rows.sort((a, b) => b.revenue - a.revenue), totals }
  }, [currentDepartmentId, cogsDate, products])

  // ── Ingredient breakdown for expanded card ──

  function getIngredientBreakdown(recipe) {
    if (!recipe || !recipe.ingredients) return []
    return recipe.ingredients.map(ing => {
      const invItem = inventoryItems.find(i => i.id === ing.inventoryItemId)
      const unitCost = invItem?.lastPurchasePrice || invItem?.avgCost || 0
      let costPerRecipeUnit = unitCost
      if (invItem?.unit === 'kg' && ing.unit === 'g') costPerRecipeUnit = unitCost / 1000
      else if (invItem?.unit === 'L' && ing.unit === 'ml') costPerRecipeUnit = unitCost / 1000
      const lineCost = costPerRecipeUnit * (Number(ing.qty) || 0)
      return {
        name: invItem?.name || ing.ingredientName || 'Unknown',
        qty: ing.qty,
        unit: ing.unit,
        unitCost: costPerRecipeUnit,
        lineCost,
      }
    })
  }

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-950 px-6 py-5">
        <div className="flex items-center gap-3 mb-1">
          <Calculator size={22} className="text-amber-400" />
          <h1 className="text-2xl font-bold tracking-tight">Costing Engine</h1>
        </div>
        <p className="text-gray-400 text-sm">Product cost cards, price simulation, and daily COGS tracking</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-800 px-6">
        <div className="flex gap-0">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-amber-400 text-amber-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-6">

        {/* ══════════════════════════════════════ */}
        {/* TAB 1: Cost Cards                     */}
        {/* ══════════════════════════════════════ */}
        {tab === 'costCards' && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative">
                <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <select
                  value={department}
                  onChange={e => setDepartment(e.target.value)}
                  className="bg-gray-900 border border-gray-800 text-sm text-white pl-9 pr-4 py-2 focus:outline-none focus:border-amber-500"
                >
                  <option value="">All Departments</option>
                  {POS_DEPARTMENTS.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 text-sm text-white pl-9 pr-4 py-2 placeholder:text-gray-600 focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-900 border border-gray-800 p-4">
                <p className="text-gray-400 text-xs mb-1">Total Products</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <p className="text-gray-400 text-xs mb-1">Avg Gross Margin</p>
                <p className="text-xl font-bold text-blue-400">{fmtPct(stats.avgMargin)}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <p className="text-gray-400 text-xs mb-1">Loss-Makers</p>
                <p className="text-xl font-bold text-red-400">{stats.lossCount}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 p-4">
                <p className="text-gray-400 text-xs mb-1">Healthy</p>
                <p className="text-xl font-bold text-green-400">{stats.healthyCount}</p>
              </div>
            </div>

            {/* Product Cost Cards Grid */}
            {filteredCards.length === 0 ? (
              <div className="bg-gray-900 border border-gray-800 p-10 text-center text-gray-500">
                <DollarSign size={32} className="mx-auto mb-3 opacity-40" />
                <p>No products found. Add products and recipes to see cost cards.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredCards.map(card => {
                  const isExpanded = expandedId === card.product.id
                  const ingredients = isExpanded ? getIngredientBreakdown(card.recipe) : []

                  return (
                    <div
                      key={card.product.id}
                      className="bg-gray-900 border border-gray-800 hover:border-gray-700 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : card.product.id)}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-white">{card.product.name}</p>
                            <p className="text-gray-500 text-xs font-mono">{card.product.code}</p>
                          </div>
                          <StatusBadge status={card.status} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500 text-xs">Selling Price</p>
                            <p className="font-semibold">{fmt(card.sellingPrice)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">COGS</p>
                            <p className="font-semibold text-amber-400">{fmt(card.cogs)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">Gross Profit</p>
                            <p className={`font-semibold ${card.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {fmt(card.grossProfit)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">Gross Margin</p>
                            <p className={`font-semibold ${
                              card.grossMargin >= 20 ? 'text-green-400' :
                              card.grossMargin >= 0 ? 'text-amber-400' : 'text-red-400'
                            }`}>
                              {fmtPct(card.grossMargin)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Ingredient Breakdown */}
                      {isExpanded && card.recipe && (
                        <div className="border-t border-gray-800 p-4 bg-gray-950/50">
                          <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">
                            Ingredient Breakdown
                            {card.recipe.batchYield > 1 && (
                              <span className="text-gray-600 normal-case tracking-normal ml-2">
                                (Batch yield: {card.recipe.batchYield})
                              </span>
                            )}
                          </p>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 border-b border-gray-800">
                                <th className="text-left py-1 font-medium">Ingredient</th>
                                <th className="text-right py-1 font-medium">Qty</th>
                                <th className="text-right py-1 font-medium">Unit Cost</th>
                                <th className="text-right py-1 font-medium">Line Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ingredients.map((ing, i) => (
                                <tr key={i} className="border-b border-gray-800/50">
                                  <td className="py-1.5 text-gray-300">{ing.name}</td>
                                  <td className="py-1.5 text-right text-gray-400">{ing.qty} {ing.unit}</td>
                                  <td className="py-1.5 text-right text-gray-400">{fmt(ing.unitCost)}</td>
                                  <td className="py-1.5 text-right font-medium text-amber-400">{fmt(ing.lineCost)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="text-gray-300 font-semibold">
                                <td className="pt-2" colSpan={3}>Total Recipe Cost</td>
                                <td className="pt-2 text-right text-amber-400">
                                  {fmt(ingredients.reduce((s, i) => s + i.lineCost, 0))}
                                </td>
                              </tr>
                              {card.recipe.batchYield > 1 && (
                                <tr className="text-gray-400">
                                  <td className="pt-1" colSpan={3}>Cost per unit (÷ {card.recipe.batchYield})</td>
                                  <td className="pt-1 text-right text-white font-semibold">
                                    {fmt(ingredients.reduce((s, i) => s + i.lineCost, 0) / card.recipe.batchYield)}
                                  </td>
                                </tr>
                              )}
                            </tfoot>
                          </table>
                        </div>
                      )}

                      {isExpanded && !card.recipe && (
                        <div className="border-t border-gray-800 p-4 bg-gray-950/50 text-center">
                          <p className="text-gray-500 text-xs">No recipe linked to this product</p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 2: Cost Simulation                */}
        {/* ══════════════════════════════════════ */}
        {tab === 'simulation' && (
          <div>
            <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sliders size={18} className="text-amber-400" />
                Ingredient Price Simulation
              </h2>
              <p className="text-gray-400 text-sm mb-5">
                Select an ingredient, enter a simulated price, and see how it impacts all affected products.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Ingredient selector */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Select Ingredient</label>
                  <select
                    value={selectedIngredient}
                    onChange={e => { setSelectedIngredient(e.target.value); setSimulatedCost('') }}
                    className="w-full bg-gray-950 border border-gray-800 text-sm text-white px-3 py-2 focus:outline-none focus:border-amber-500"
                  >
                    <option value="">-- Choose ingredient --</option>
                    {inventoryItems
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.unit})
                        </option>
                      ))}
                  </select>
                </div>

                {/* Current cost */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Current Cost per {selectedIngredientData?.unit || 'unit'}</label>
                  <div className="bg-gray-950 border border-gray-800 text-sm text-gray-300 px-3 py-2">
                    {selectedIngredientData
                      ? fmt(selectedIngredientData.lastPurchasePrice || selectedIngredientData.avgCost || 0)
                      : '—'}
                  </div>
                </div>

                {/* Simulated cost */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Simulated Cost per {selectedIngredientData?.unit || 'unit'}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter new price"
                    value={simulatedCost}
                    onChange={e => setSimulatedCost(e.target.value)}
                    disabled={!selectedIngredient}
                    className="w-full bg-gray-950 border border-gray-800 text-sm text-white px-3 py-2 placeholder:text-gray-600 focus:outline-none focus:border-amber-500 disabled:opacity-40"
                  />
                </div>
              </div>
            </div>

            {/* Simulation Results */}
            {simulationResults.length > 0 ? (
              <div className="bg-gray-900 border border-gray-800">
                <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-300">
                    {simulationResults.length} Affected Product{simulationResults.length !== 1 ? 's' : ''}
                  </p>
                  {simulationResults.some(r => r.becomesLoss) && (
                    <span className="flex items-center gap-1.5 text-xs text-red-400">
                      <AlertTriangle size={13} />
                      {simulationResults.filter(r => r.becomesLoss).length} become loss-making
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs border-b border-gray-800">
                        <th className="text-left px-5 py-2 font-medium">Product</th>
                        <th className="text-right px-5 py-2 font-medium">Selling Price</th>
                        <th className="text-right px-5 py-2 font-medium">Current COGS</th>
                        <th className="text-right px-5 py-2 font-medium">New COGS</th>
                        <th className="text-right px-5 py-2 font-medium">Current Margin</th>
                        <th className="text-right px-5 py-2 font-medium">New Margin</th>
                        <th className="text-right px-5 py-2 font-medium">Min Price (30%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulationResults.map(row => (
                        <tr
                          key={row.product.id}
                          className={`border-b border-gray-800/50 ${row.becomesLoss ? 'bg-red-950/20' : ''}`}
                        >
                          <td className="px-5 py-2.5">
                            <p className="font-medium text-white">{row.product.name}</p>
                            <p className="text-gray-600 text-xs font-mono">{row.product.code}</p>
                          </td>
                          <td className="px-5 py-2.5 text-right">{fmt(row.product.sellingPrice)}</td>
                          <td className="px-5 py-2.5 text-right text-gray-400">{fmt(row.currentCogs)}</td>
                          <td className="px-5 py-2.5 text-right text-amber-400">{fmt(row.simCogs)}</td>
                          <td className="px-5 py-2.5 text-right">
                            <span className={row.currentMargin >= 20 ? 'text-green-400' : row.currentMargin >= 0 ? 'text-amber-400' : 'text-red-400'}>
                              {fmtPct(row.currentMargin)}
                            </span>
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {row.simMargin < row.currentMargin ? (
                                <TrendingDown size={13} className="text-red-400" />
                              ) : (
                                <TrendingUp size={13} className="text-green-400" />
                              )}
                              <span className={row.simMargin >= 20 ? 'text-green-400' : row.simMargin >= 0 ? 'text-amber-400' : 'text-red-400'}>
                                {fmtPct(row.simMargin)}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            {row.needsPriceIncrease ? (
                              <span className="text-amber-300 font-semibold">{fmt(row.minSellingPrice)}</span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : selectedIngredient && simulatedCost !== '' ? (
              <div className="bg-gray-900 border border-gray-800 p-10 text-center text-gray-500">
                <Calculator size={32} className="mx-auto mb-3 opacity-40" />
                <p>No products use this ingredient in their recipes.</p>
              </div>
            ) : (
              <div className="bg-gray-900 border border-gray-800 p-10 text-center text-gray-500">
                <Sliders size={32} className="mx-auto mb-3 opacity-40" />
                <p>Select an ingredient and enter a simulated price to see impact.</p>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 3: Daily COGS                     */}
        {/* ══════════════════════════════════════ */}
        {tab === 'dailyCogs' && (
          <div>
            {/* Date selector */}
            <div className="flex items-center gap-3 mb-6">
              <label className="text-sm text-gray-400">Date:</label>
              <input
                type="date"
                value={cogsDate}
                onChange={e => setCogsDate(e.target.value)}
                className="bg-gray-900 border border-gray-800 text-sm text-white px-3 py-2 focus:outline-none focus:border-amber-500"
              />
            </div>

            {dailyCogsData.rows.length === 0 ? (
              <div className="bg-gray-900 border border-gray-800 p-10 text-center text-gray-500">
                <DollarSign size={32} className="mx-auto mb-3 opacity-40" />
                <p>No sales recorded for this date.</p>
              </div>
            ) : (
              <div className="bg-gray-900 border border-gray-800 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-800">
                      <th className="text-left px-5 py-3 font-medium">Product</th>
                      <th className="text-right px-5 py-3 font-medium">Qty Sold</th>
                      <th className="text-right px-5 py-3 font-medium">Revenue</th>
                      <th className="text-right px-5 py-3 font-medium">Theoretical COGS</th>
                      <th className="text-right px-5 py-3 font-medium">Actual COGS</th>
                      <th className="text-right px-5 py-3 font-medium">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyCogsData.rows.map(row => (
                      <tr key={row.productId} className={`border-b border-gray-800/50 ${row.flagged ? 'bg-red-950/15' : ''}`}>
                        <td className="px-5 py-2.5 font-medium text-white">{row.productName}</td>
                        <td className="px-5 py-2.5 text-right text-gray-300">{row.qty}</td>
                        <td className="px-5 py-2.5 text-right text-gray-300">{fmt(row.revenue)}</td>
                        <td className="px-5 py-2.5 text-right text-gray-400">{fmt(row.theoreticalCogs)}</td>
                        <td className="px-5 py-2.5 text-right text-amber-400">{fmt(row.actualCogs)}</td>
                        <td className="px-5 py-2.5 text-right">
                          <span className={`flex items-center justify-end gap-1 ${
                            row.flagged ? 'text-red-400 font-semibold' : 'text-gray-400'
                          }`}>
                            {row.flagged && <AlertTriangle size={13} />}
                            {fmtPct(row.variance)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-700 font-semibold">
                      <td className="px-5 py-3 text-white">TOTAL</td>
                      <td className="px-5 py-3 text-right text-white">{dailyCogsData.totals.qty}</td>
                      <td className="px-5 py-3 text-right text-white">{fmt(dailyCogsData.totals.revenue)}</td>
                      <td className="px-5 py-3 text-right text-gray-300">{fmt(dailyCogsData.totals.theoreticalCogs)}</td>
                      <td className="px-5 py-3 text-right text-amber-400">{fmt(dailyCogsData.totals.actualCogs)}</td>
                      <td className="px-5 py-3 text-right">
                        <span className={`flex items-center justify-end gap-1 ${
                          Math.abs(dailyCogsData.totals.variance) > 15 ? 'text-red-400' : 'text-gray-400'
                        }`}>
                          {Math.abs(dailyCogsData.totals.variance) > 15 && <AlertTriangle size={13} />}
                          {fmtPct(dailyCogsData.totals.variance)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
